// src/admin/OldBaptistImport.jsx
// One-time import tool for Old Baptist Cemetery, Coram NY.
// Paste the Carter 1962-1970 transcription text, extract via Claude, review, generate SQL.

import { useState, useEffect } from 'react'
import { OBC_CEMETERY_ID, OBC_CARTER_SOURCE_ID, AI_REL_TO_DB, AI_REL_TO_DB_INVERSE } from '../constants'

// ── SQL helpers ───────────────────────────────────────────────────────────────

const esc = (s) => (s || '').replace(/'/g, "''")
const q = (s) => (s != null && s !== '') ? `'${esc(String(s))}'` : 'NULL'

// Fixed 2026-10-22: was a naive `_of`-strip (PARENT_OF -> 'parent'), backward
// per the AI_REL_TO_DB convention in constants.js — see
// docs/data-quality/kinship-import-pipeline-findings-2026-10-22.md

function generateSQL(entries) {
  const enabled = entries.filter(e => e.enabled && !e.unreadable)
  if (!enabled.length) return ''

  const today = new Date().toISOString().split('T')[0]
  const lines = [
    `-- Old Baptist Cemetery import — Carter transcription 1962–1970`,
    `-- Generated: ${today}`,
    `-- Cemetery ID: ${OBC_CEMETERY_ID}`,
    `-- Source ID:   ${OBC_CARTER_SOURCE_ID}`,
    `-- Idempotent: safe to re-run after prerequisite indexes are created`,
    ``,
    `-- ── PREREQUISITES (run once in Supabase SQL editor) ──────────────────────────`,
    `-- CREATE UNIQUE INDEX IF NOT EXISTS idx_stones_legacy_obc`,
    `--   ON stones(legacy_stone_id) WHERE legacy_stone_id LIKE 'OBC%';`,
    `-- CREATE UNIQUE INDEX IF NOT EXISTS idx_deceased_legacy_obc`,
    `--   ON deceased(legacy_id) WHERE legacy_id LIKE 'OBC%';`,
    ``,
    `BEGIN;`,
    ``,
    `-- ── STEP 1: Stones ───────────────────────────────────────────────────────────`,
  ]

  for (const entry of enabled) {
    const stRef = `OBC_${entry.entry_number}`
    lines.push(
      `INSERT INTO stones (stone_id, cemetery_id, legacy_stone_id, plot_number, condition_notes)`,
      `VALUES (gen_random_uuid(), '${OBC_CEMETERY_ID}', ${q(stRef)}, ${q(String(entry.entry_number))}, ${q(entry.stone_condition)})`,
      `ON CONFLICT (legacy_stone_id) WHERE legacy_stone_id LIKE 'OBC%' DO NOTHING;`,
      ``,
    )
  }

  lines.push(`-- ── STEP 2: Occupants ───────────────────────────────────────────────────────`)

  for (const entry of enabled) {
    for (const occ of (entry.occupants || [])) {
      if (!occ.obc_ref) continue
      const displayName = [occ.title, occ.first_name, occ.middle_name, occ.last_name].filter(Boolean).join(' ')
      lines.push(
        `-- ${displayName} (${occ.obc_ref})`,
        `INSERT INTO deceased (deceased_id, cemetery_id, source_id, legacy_id,`,
        `  title, first_name, middle_name, last_name, maiden_name,`,
        `  date_of_birth_verbatim, date_of_birth_year,`,
        `  date_of_death_verbatim, date_of_death_year, notes)`,
        `VALUES (gen_random_uuid(), '${OBC_CEMETERY_ID}', '${OBC_CARTER_SOURCE_ID}', ${q(occ.obc_ref)},`,
        `  ${q(occ.title)}, ${q(occ.first_name)}, ${q(occ.middle_name)}, ${q(occ.last_name)}, ${q(occ.maiden_name)},`,
        `  ${q(occ.date_of_birth_verbatim)}, ${occ.date_of_birth_year ?? 'NULL'},`,
        `  ${q(occ.date_of_death_verbatim)}, ${occ.date_of_death_year ?? 'NULL'}, ${q(occ.notes)})`,
        `ON CONFLICT (legacy_id) WHERE legacy_id LIKE 'OBC%' DO NOTHING;`,
        ``,
      )
    }
  }

  const hasMentioned = enabled.some(e => e.mentioned?.length)
  if (hasMentioned) {
    lines.push(`-- ── STEP 3: Mentioned persons ──────────────────────────────────────────────`)
    for (const entry of enabled) {
      for (const m of (entry.mentioned || [])) {
        if (!m.obc_ref) continue
        const displayName = [m.first_name, m.last_name].filter(Boolean).join(' ')
        lines.push(
          `-- ${displayName} (${m.obc_ref}) — mentioned`,
          `INSERT INTO deceased (deceased_id, cemetery_id, source_id, legacy_id, first_name, last_name, maiden_name)`,
          `VALUES (gen_random_uuid(), '${OBC_CEMETERY_ID}', '${OBC_CARTER_SOURCE_ID}', ${q(m.obc_ref)},`,
          `  ${q(m.first_name)}, ${q(m.last_name)}, ${q(m.maiden_name)})`,
          `ON CONFLICT (legacy_id) WHERE legacy_id LIKE 'OBC%' DO NOTHING;`,
          ``,
        )
      }
    }
  }

  lines.push(`-- ── STEP 4: Stone–occupant links ────────────────────────────────────────────`)
  for (const entry of enabled) {
    const stRef = `OBC_${entry.entry_number}`
    for (const occ of (entry.occupants || [])) {
      if (!occ.obc_ref) continue
      lines.push(
        `INSERT INTO stone_deceased (stone_id, deceased_id, role, match_method)`,
        `SELECT s.stone_id, d.deceased_id, 'occupant', 'manual'`,
        `FROM stones s, deceased d`,
        `WHERE s.legacy_stone_id = ${q(stRef)} AND d.legacy_id = ${q(occ.obc_ref)}`,
        `ON CONFLICT DO NOTHING;`,
        ``,
      )
    }
  }

  if (hasMentioned) {
    lines.push(`-- ── STEP 5: Kinship ─────────────────────────────────────────────────────────`)
    for (const entry of enabled) {
      for (const m of (entry.mentioned || [])) {
        if (!m.relationship_type || !m.related_to_ref || !m.obc_ref) continue
        const dbRel = AI_REL_TO_DB[m.relationship_type]
        const dbInv = AI_REL_TO_DB_INVERSE[m.relationship_type]
        if (!dbRel || !dbInv) continue
        lines.push(
          `INSERT INTO kinship (primary_deceased_id, relative_deceased_id, relationship_type, source, confidence, source_id)`,
          `SELECT a.deceased_id, b.deceased_id, '${dbRel}', 'stone_inscription', 'confirmed', '${OBC_CARTER_SOURCE_ID}'`,
          `FROM deceased a, deceased b WHERE a.legacy_id = ${q(m.obc_ref)} AND b.legacy_id = ${q(m.related_to_ref)}`,
          `ON CONFLICT DO NOTHING;`,
          `INSERT INTO kinship (primary_deceased_id, relative_deceased_id, relationship_type, source, confidence, source_id)`,
          `SELECT b.deceased_id, a.deceased_id, '${dbInv}', 'stone_inscription', 'confirmed', '${OBC_CARTER_SOURCE_ID}'`,
          `FROM deceased a, deceased b WHERE a.legacy_id = ${q(m.obc_ref)} AND b.legacy_id = ${q(m.related_to_ref)}`,
          `ON CONFLICT DO NOTHING;`,
          ``,
        )
      }
    }
  }

  lines.push(`-- ── STEP 6: Source references ───────────────────────────────────────────────`)
  for (const entry of enabled) {
    for (const occ of (entry.occupants || [])) {
      if (!occ.obc_ref) continue
      lines.push(
        `INSERT INTO deceased_sources (deceased_id, source_id, source_type, date_of_death_verbatim, date_of_death_year)`,
        `SELECT d.deceased_id, '${OBC_CARTER_SOURCE_ID}', 'stone_inscription',`,
        `  ${q(occ.date_of_death_verbatim)}, ${occ.date_of_death_year ?? 'NULL'}`,
        `FROM deceased d WHERE d.legacy_id = ${q(occ.obc_ref)}`,
        `ON CONFLICT DO NOTHING;`,
        ``,
      )
    }
  }

  lines.push(`COMMIT;`)
  return lines.join('\n')
}

// ── Inline edit helpers ───────────────────────────────────────────────────────

function EditText({ value, onChange, placeholder }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value || '')
  const commit = () => { setEditing(false); onChange(draft) }
  if (editing) {
    return (
      <input autoFocus value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Escape') commit() }}
        style={{ background: '#0f172a', border: '1px solid #3b82f6', borderRadius: 3, color: '#f1f5f9', padding: '1px 5px', fontSize: 'inherit', fontFamily: 'inherit', outline: 'none', minWidth: 60 }} />
    )
  }
  return (
    <span onClick={() => { setDraft(value || ''); setEditing(true) }}
      title="Click to edit"
      style={{ cursor: 'text', borderBottom: '1px dashed #334155', display: 'inline-block', minWidth: 16 }}>
      {value || <span style={{ color: '#475569', fontStyle: 'italic' }}>{placeholder || '—'}</span>}
    </span>
  )
}

function EditYear({ value, onChange }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value != null ? String(value) : '')
  const commit = () => { setEditing(false); const n = parseInt(draft, 10); onChange(isNaN(n) ? null : n) }
  if (editing) {
    return (
      <input autoFocus type="number" value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Escape') commit() }}
        style={{ background: '#0f172a', border: '1px solid #3b82f6', borderRadius: 3, color: '#f1f5f9', padding: '1px 4px', fontSize: 'inherit', outline: 'none', width: 64 }} />
    )
  }
  return (
    <span onClick={() => { setDraft(value != null ? String(value) : ''); setEditing(true) }}
      title="Click to edit year"
      style={{ cursor: 'text', borderBottom: '1px dashed #334155' }}>
      {value ?? '?'}
    </span>
  )
}

// ── Entry card ────────────────────────────────────────────────────────────────

function EntryCard({ entry, onToggle, onUpdate }) {
  const [open, setOpen] = useState(false)
  const { entry_number, unreadable, stone_condition, occupants = [], mentioned = [], enabled } = entry

  const primaryName = occupants[0]
    ? [occupants[0].first_name, occupants[0].last_name].filter(Boolean).join(' ')
    : unreadable ? '(unreadable)' : '(no name)'

  const updateOcc = (oi, field, val) =>
    onUpdate({ ...entry, occupants: occupants.map((o, i) => i === oi ? { ...o, [field]: val } : o) })

  return (
    <div style={{ background: enabled ? '#1e293b' : '#111827', border: `1px solid ${enabled ? '#1e3a5f' : '#374151'}`, borderRadius: 8, marginBottom: 8, opacity: enabled ? 1 : 0.5 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: 'pointer' }}
        onClick={() => setOpen(o => !o)}>
        <span style={{ background: '#1e3a5f', color: '#60a5fa', borderRadius: 4, padding: '2px 8px', fontWeight: 700, fontSize: 13, fontFamily: 'monospace', minWidth: 28, textAlign: 'center' }}>
          {entry_number}
        </span>
        <span style={{ flex: 1, color: '#f1f5f9', fontWeight: 600, fontSize: 14 }}>
          {primaryName}
          {occupants.length > 1 && <span style={{ fontSize: 11, color: '#64748b', marginLeft: 6 }}>+{occupants.length - 1} more</span>}
        </span>
        {occupants[0]?.date_of_death_year && (
          <span style={{ color: '#64748b', fontSize: 12 }}>d. {occupants[0].date_of_death_year}</span>
        )}
        {mentioned.length > 0 && (
          <span style={{ fontSize: 11, color: '#475569' }}>{mentioned.length} rel.</span>
        )}
        {stone_condition && (
          <span style={{ fontSize: 11, color: '#f59e0b', background: '#451a03', borderRadius: 4, padding: '1px 6px' }}>
            {stone_condition.length > 22 ? stone_condition.slice(0, 22) + '…' : stone_condition}
          </span>
        )}
        <button onClick={e => { e.stopPropagation(); onToggle() }}
          style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, border: '1px solid #374151', background: enabled ? '#15803d' : '#374151', color: '#fff', cursor: 'pointer' }}>
          {enabled ? 'include' : 'skip'}
        </button>
        <span style={{ color: '#64748b', fontSize: 14 }}>{open ? '▲' : '▼'}</span>
      </div>

      {open && (
        <div style={{ padding: '0 14px 14px', borderTop: '1px solid #0f172a' }}>
          {stone_condition && (
            <div style={{ marginTop: 8, fontSize: 12, color: '#f59e0b' }}>Condition: {stone_condition}</div>
          )}

          {unreadable && (
            <div style={{ marginTop: 8, color: '#ef4444', fontSize: 12 }}>Stone unreadable — no deceased data extracted.</div>
          )}

          {occupants.map((occ, oi) => (
            <div key={oi} style={{ marginTop: 10, padding: '8px 10px', background: '#0f172a', borderRadius: 6, borderLeft: '3px solid #4ade80' }}>
              <div style={{ color: '#4ade80', fontSize: 11, marginBottom: 6, fontWeight: 600 }}>
                {occupants.length > 1 ? `OCCUPANT ${oi + 1}` : 'OCCUPANT'} · <span style={{ fontFamily: 'monospace', color: '#334155' }}>{occ.obc_ref}</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 13 }}>
                {[['Title', 'title'], ['First', 'first_name'], ['Middle', 'middle_name'], ['Last', 'last_name'], ['Maiden', 'maiden_name']].map(([label, field]) => (
                  <label key={field} style={{ color: '#64748b', fontSize: 11 }}>{label}
                    <div><EditText value={occ[field]} onChange={v => updateOcc(oi, field, v)} /></div>
                  </label>
                ))}
                <label style={{ color: '#64748b', fontSize: 11 }}>Born
                  <div><EditYear value={occ.date_of_birth_year} onChange={v => updateOcc(oi, 'date_of_birth_year', v)} /></div>
                </label>
                <label style={{ color: '#64748b', fontSize: 11 }}>Died
                  <div><EditYear value={occ.date_of_death_year} onChange={v => updateOcc(oi, 'date_of_death_year', v)} /></div>
                </label>
              </div>
              {occ.date_of_death_verbatim && (
                <div style={{ fontSize: 11, color: '#475569', marginTop: 4 }}>d. {occ.date_of_death_verbatim}</div>
              )}
              {occ.notes && (
                <div style={{ fontSize: 11, color: '#475569', marginTop: 4 }}>{occ.notes}</div>
              )}
            </div>
          ))}

          {mentioned.length > 0 && (
            <div style={{ marginTop: 10, padding: '8px 10px', background: '#0f172a', borderRadius: 6, borderLeft: '3px solid #334155' }}>
              <div style={{ color: '#64748b', fontSize: 11, marginBottom: 6, fontWeight: 600 }}>MENTIONED PERSONS ({mentioned.length})</div>
              {mentioned.map((m, mi) => {
                const occName = occupants.find(o => o.obc_ref === m.related_to_ref)?.first_name || occupants[0]?.first_name || 'occupant'
                const relLabel = {
                  PARENT_OF: `parent of ${occName}`,
                  CHILD_OF:  `child of ${occName}`,
                  SPOUSE:    `spouse of ${occName}`,
                  SIBLING_OF:`sibling of ${occName}`,
                }[m.relationship_type] ?? m.relationship_type
                return (
                  <div key={mi} style={{ fontSize: 12, color: '#94a3b8', marginTop: 4, display: 'flex', gap: 8, alignItems: 'baseline' }}>
                    <span style={{ color: '#475569', fontSize: 10, minWidth: 110 }}>{relLabel}</span>
                    <span>
                      {[m.first_name, m.last_name].filter(Boolean).join(' ')}
                      {m.maiden_name && <span style={{ color: '#475569' }}> (née {m.maiden_name})</span>}
                    </span>
                    <span style={{ color: '#1e3a5f', fontSize: 10, fontFamily: 'monospace' }}>{m.obc_ref}</span>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

const PASTE_PLACEHOLDER =
`Paste the full Carter transcription text here (all 5 pages).

Example:
1. BENJ. OVERTON d. 9 Jan. 1849 in 77 th year. Note: He was son of JAMES & PHEBE (ROSE) OVERTON.
2. ABIGAIL OVERTON d. 30 March 1869 age 83 years 3 mo. f.s. A.0. Stone is face down on ground...`

export default function OldBaptistImport({ onBack }) {
  const [text, setText] = useState(() => localStorage.getItem('obc_import_text') || '')
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState(null)
  const [moreError, setMoreError] = useState(null)
  const [entries, setEntries] = useState(() => {
    try { const s = localStorage.getItem('obc_import_entries'); return s ? JSON.parse(s) : null } catch { return null }
  })
  const [moreText, setMoreText] = useState('')
  const [sql, setSql] = useState('')
  const [copied, setCopied] = useState(false)

  useEffect(() => { localStorage.setItem('obc_import_text', text) }, [text])
  useEffect(() => {
    if (entries !== null) localStorage.setItem('obc_import_entries', JSON.stringify(entries))
    else localStorage.removeItem('obc_import_entries')
  }, [entries])

  const extract = async () => {
    if (!text.trim()) return
    setLoading(true)
    setError(null)
    setEntries(null)
    setSql('')
    try {
      const res = await fetch('/api/extractOldBaptist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: text.trim() }),
      })
      const data = await res.json()
      if (!res.ok) { setError(data.error || 'Extraction failed'); return }
      const withEnabled = (data.entries || []).map(e => ({ ...e, enabled: true }))
      setEntries(withEnabled)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const extractMore = async () => {
    if (!moreText.trim()) return
    setLoadingMore(true)
    setMoreError(null)
    try {
      const res = await fetch('/api/extractOldBaptist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: moreText.trim() }),
      })
      const data = await res.json()
      if (!res.ok) { setMoreError(data.error || 'Extraction failed'); return }
      const newEntries = (data.entries || []).map(e => ({ ...e, enabled: true }))
      setEntries(prev => [...(prev || []), ...newEntries])
      setMoreText('')
    } catch (err) {
      setMoreError(err.message)
    } finally {
      setLoadingMore(false)
    }
  }

  const toggleEntry = (i) =>
    setEntries(prev => prev.map((e, j) => j === i ? { ...e, enabled: !e.enabled } : e))

  const updateEntry = (i, updated) =>
    setEntries(prev => prev.map((e, j) => j === i ? updated : e))

  const genSQL = () => { if (entries) setSql(generateSQL(entries)) }

  const copySQL = () => {
    if (!sql) return
    navigator.clipboard.writeText(sql).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  const enabledCount  = entries ? entries.filter(e => e.enabled).length : 0
  const occupantCount = entries ? entries.filter(e => e.enabled).reduce((n, e) => n + (e.occupants?.length || 0), 0) : 0
  const mentionedCount = entries ? entries.filter(e => e.enabled).reduce((n, e) => n + (e.mentioned?.length || 0), 0) : 0

  return (
    <div style={{ minHeight: '100vh', background: '#0f172a', color: '#f1f5f9', fontFamily: 'system-ui, sans-serif' }}>
      <div style={{ background: '#1e293b', padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid #0f172a' }}>
        <button onClick={onBack} style={{ color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14 }}>
          ← Admin
        </button>
        <div>
          <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#4ade80' }}>Old Baptist Cemetery Import</h1>
          <p style={{ margin: 0, fontSize: 12, color: '#64748b' }}>Carter transcription 1962–1970 · Coram, NY</p>
        </div>
      </div>

      <div style={{ maxWidth: 800, margin: '0 auto', padding: 20 }}>

        {/* Step 1 — run SQL setup first */}
        <div style={{ background: '#1c1917', border: '1px solid #451a03', borderRadius: 8, padding: 14, marginBottom: 20, fontSize: 13 }}>
          <div style={{ color: '#f59e0b', fontWeight: 700, marginBottom: 6 }}>Before first run — set up cemetery & source in Supabase</div>
          <div style={{ color: '#92400e', fontSize: 12, marginBottom: 8 }}>Run this once in the Supabase SQL editor, then proceed below.</div>
          <pre style={{ margin: 0, color: '#d97706', fontSize: 11, overflowX: 'auto', background: '#0f172a', borderRadius: 4, padding: 10 }}>{`INSERT INTO cemeteries (cemetery_id, name, town, state, country, notes)
VALUES (
  '${OBC_CEMETERY_ID}',
  'Old Baptist Cemetery', 'Coram', 'NY', 'USA',
  'Near intersection of Route 25 and Route 112. GPS: 40.869806, -73.001229.'
) ON CONFLICT (cemetery_id) DO NOTHING;

INSERT INTO sources (source_id, source_type, title, date_range, reliability, notes)
VALUES (
  '${OBC_CARTER_SOURCE_ID}',
  'stone_inscription',
  'Old Baptist Cemetery, Coram NY — Elbert N. Carter transcription',
  '1962-1970',
  'transcription',
  'Stones copied by Elbert N. Carter June 1962, rechecked April 1970. Via interment.net.'
) ON CONFLICT (source_id) DO NOTHING;`}
          </pre>
        </div>

        {/* Step 2 — paste text */}
        {!entries && (
          <div style={{ background: '#1e293b', borderRadius: 10, padding: 20, marginBottom: 20 }}>
            <h2 style={{ margin: '0 0 12px', fontSize: 15, color: '#94a3b8', fontWeight: 600 }}>Paste transcription text</h2>
            <textarea
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder={PASTE_PLACEHOLDER}
              rows={18}
              style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: 6, color: '#f1f5f9', padding: 12, fontSize: 13, fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box' }}
            />
            <div style={{ marginTop: 12, display: 'flex', gap: 10, alignItems: 'center' }}>
              <button
                onClick={extract}
                disabled={loading || !text.trim()}
                style={{ background: loading || !text.trim() ? '#374151' : '#15803d', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 20px', cursor: loading || !text.trim() ? 'default' : 'pointer', fontWeight: 600, fontSize: 14 }}>
                {loading ? 'Extracting with Claude…' : 'Extract with Claude'}
              </button>
              {text.trim() && !loading && (
                <button onClick={() => setText('')}
                  style={{ background: 'none', border: '1px solid #374151', color: '#94a3b8', borderRadius: 6, padding: '10px 12px', cursor: 'pointer', fontSize: 13 }}>
                  Clear
                </button>
              )}
              {error && <span style={{ color: '#ef4444', fontSize: 13 }}>Error: {error}</span>}
            </div>
          </div>
        )}

        {/* Step 3 — review */}
        {entries && (
          <>
            <div style={{ background: '#1e293b', borderRadius: 10, padding: '14px 18px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
              <div>
                <span style={{ color: '#4ade80', fontWeight: 700, fontSize: 22 }}>{enabledCount}</span>
                <span style={{ color: '#64748b', fontSize: 13, marginLeft: 4 }}>entries</span>
                <span style={{ color: '#4ade80', fontWeight: 700, fontSize: 22, marginLeft: 16 }}>{occupantCount}</span>
                <span style={{ color: '#64748b', fontSize: 13, marginLeft: 4 }}>occupants</span>
                <span style={{ color: '#64748b', fontWeight: 700, fontSize: 16, marginLeft: 16 }}>{mentionedCount}</span>
                <span style={{ color: '#64748b', fontSize: 13, marginLeft: 4 }}>mentioned</span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => { setEntries(null); setSql('') }}
                  style={{ background: 'none', border: '1px solid #374151', color: '#94a3b8', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 13 }}>
                  ← Re-extract
                </button>
                <button onClick={() => setEntries(prev => prev.map(e => ({ ...e, enabled: true })))}
                  style={{ background: 'none', border: '1px solid #374151', color: '#94a3b8', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 13 }}>
                  Enable all
                </button>
              </div>
            </div>

            {entries.map((entry, i) => (
              <EntryCard
                key={entry.entry_number}
                entry={entry}
                onToggle={() => toggleEntry(i)}
                onUpdate={(updated) => updateEntry(i, updated)}
              />
            ))}

            {/* Add more entries */}
            <div style={{ background: '#1e293b', borderRadius: 10, padding: 16, marginTop: 16 }}>
              <h2 style={{ margin: '0 0 10px', fontSize: 14, color: '#64748b', fontWeight: 600 }}>Add more entries (paste next chunk)</h2>
              <textarea
                value={moreText}
                onChange={e => setMoreText(e.target.value)}
                placeholder="Paste the next section of the transcription here..."
                rows={6}
                style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: 6, color: '#f1f5f9', padding: 10, fontSize: 13, fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box' }}
              />
              <div style={{ marginTop: 8, display: 'flex', gap: 10, alignItems: 'center' }}>
                <button
                  onClick={extractMore}
                  disabled={loadingMore || !moreText.trim()}
                  style={{ background: loadingMore || !moreText.trim() ? '#374151' : '#1e3a5f', color: '#60a5fa', border: '1px solid #1e40af', borderRadius: 6, padding: '8px 16px', cursor: loadingMore || !moreText.trim() ? 'default' : 'pointer', fontWeight: 600, fontSize: 13 }}>
                  {loadingMore ? 'Extracting…' : 'Extract & append'}
                </button>
                {moreError && <span style={{ color: '#ef4444', fontSize: 13 }}>Error: {moreError}</span>}
              </div>
            </div>

            {/* Step 4 — generate SQL */}
            <div style={{ background: '#1e293b', borderRadius: 10, padding: 20, marginTop: 16 }}>
              <h2 style={{ margin: '0 0 12px', fontSize: 15, color: '#94a3b8', fontWeight: 600 }}>Generate SQL</h2>
              <button onClick={genSQL}
                style={{ background: '#1e3a5f', color: '#60a5fa', border: '1px solid #1e40af', borderRadius: 6, padding: '10px 20px', cursor: 'pointer', fontWeight: 600, fontSize: 14, marginBottom: 12 }}>
                Generate SQL ({enabledCount} entries · {occupantCount} occupants)
              </button>

              {sql && (
                <>
                  <div style={{ marginBottom: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button onClick={copySQL}
                      style={{ background: copied ? '#15803d' : '#334155', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                      {copied ? '✓ Copied!' : 'Copy SQL'}
                    </button>
                    <span style={{ color: '#475569', fontSize: 12 }}>Paste into Supabase SQL Editor · runs in a transaction</span>
                  </div>
                  <textarea
                    readOnly
                    value={sql}
                    rows={22}
                    style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: 6, color: '#94a3b8', padding: 12, fontSize: 11, fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box' }}
                  />
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
